import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createSlots as _createSlots, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f6a67e64"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "alert alert-primary alert-sm source-error"
}
const _hoisted_2 = {
  key: 1,
  class: "file-area"
}
const _hoisted_3 = {
  key: 2,
  class: "alert alert-primary alert-sm not-supported"
}
const _hoisted_4 = {
  key: 2,
  class: "no-file"
}
const _hoisted_5 = ["onDrop"]
const _hoisted_6 = ["accept"]
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "drop-instruction" }, "Drag here or choose a file", -1))
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "btn btn-primary choose-btn" }, "Choose a file", -1))
const _hoisted_9 = { class: "supported-types" }
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("button", {
  type: "button",
  class: "btn btn-link",
  "data-bs-toggle": "collapse",
  "data-bs-target": "#supportGrid",
  "aria-expanded": "false",
  "aria-controls": "supportGrid"
}, " What genealogy file types are supported? ", -1))
const _hoisted_11 = {
  id: "supportGrid",
  class: "collapse"
}
const _hoisted_12 = {
  key: 3,
  class: "upload-progress"
}
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "upload-status" }, "Uploading your tree...", -1))

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { usePageTitle, unexpectedErrorEvent } from '@webapp/util/AppUtil'
import { useTelemetry } from '@webapp/util/Telemetry'
import { useConfirmation } from '@webapp/util/Confirmation'
import { useSyncSessionStore } from './SyncSessionStore'
import { useWorkspaceFinder, WorkspaceMatch } from './WorkspaceFinder'
import { SourceState, toSyncSource } from './SyncSessionModel'
import { currentOS, getSupportedFileExtensions, workspaceTypeInfos } from '@/rd/ResearchDataModel'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { isTestStringId } from '@webapp/util/TestId'
import TaskPage from '@webapp/util/TaskPage.vue'
import ProgressBar from '@webapp/util/ProgressBar.vue'
import Modal from '@webapp/util/Modal.vue'
import SyncSourceHeader from './SyncSourceHeader.vue'
import WorkspaceMatchModal from './WorkspaceMatchModal.vue'
import AppSupportGrid from '@/public/AppSupportGrid.vue'
import { AsyncUtil } from '@webapp/util/AsyncUtil'


export default /*#__PURE__*/_defineComponent({
  __name: 'SyncFilePage',
  emits: ['uploaded', 'back'],
  setup(__props, { emit }) {


const router = useRouter()
const syncSessionStore = useSyncSessionStore()
const tokenManager = useTokenManager()
const workspaceFinder = useWorkspaceFinder()
const { trackException } = useTelemetry()

const isTestUserRef = computed(() => isTestStringId(tokenManager.current.userId))

const isUploadingRef = ref(false)
const uploadPercentRef = ref(0)
const isDragOverRef = ref(false)
const sourceErrorRef = ref<string>()
const selectedFileRef = ref<File>()
const sessionIdRef = ref<string>()
const sourceRef = computed(() => selectedFileRef.value ? toSyncSource(selectedFileRef.value) : undefined)
const taskPageRef = ref<InstanceType<typeof TaskPage>>()
const abandonModalRef = ref<InstanceType<typeof Modal>>()
const matchModalRef = ref<InstanceType<typeof WorkspaceMatchModal>>()
const matchesRef = ref<WorkspaceMatch[]>([])
const pendingFileRef = ref<File>()
const abandonConfirmation = useConfirmation(abandonModalRef)

const selectedTypeInfoRef = computed(() =>
  selectedFileRef.value ? typeInfoForName(selectedFileRef.value.name) : undefined)

function typeInfoForName(name: string) {
  const m = name.match(/\.[^.]+$/)
  const ext = m ? m[0].toLowerCase() : ''
  const typeInfo = ext ? workspaceTypeInfos.find(t => t.fileExtensions.includes(ext)) : undefined
  return typeInfo && (!typeInfo.hidden || isTestUserRef.value) ? typeInfo : undefined
}

const isFileSupportedRef = computed(() =>
  !!currentOS && !!selectedTypeInfoRef.value?.supportedOn.includes(currentOS),
)

const acceptAttrRef = computed(() => getSupportedFileExtensions(currentOS, isTestUserRef.value).join(','))

let abortController: AbortController | undefined

usePageTitle('Upload my tree')

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = '' // let the same file be re-picked after a cancel
  if (file) {
    selectFile(file)
  }
}

function onFileDropped(event: DragEvent) {
  isDragOverRef.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) {
    selectFile(file)
  }
}

// A same-named existing tree means this is really a re-sync, which is done on that tree's manage
// page -- offer that before committing the file. Best-effort: a failure just selects the file.
async function selectFile(file: File) {
  sourceErrorRef.value = undefined
  try {
    const matches = await workspaceFinder.findWorkspacesWithFileNameAsync(file.name)
    if (matches.length) {
      matchesRef.value = matches
      pendingFileRef.value = file
      matchModalRef.value?.open()
      return
    }
  }
  catch (error) {
    trackException(error)
  }
  commitFile(file)
}

// Committing a supported file starts its upload right away -- there's no separate confirm step.
// An unsupported file just stays selected so the "not supported" notice can prompt a change.
function commitFile(file: File) {
  selectedFileRef.value = file
  if (isFileSupportedRef.value) {
    onUpload()
  }
}

function onChangeFile() {
  selectedFileRef.value = undefined
}

async function onUpload() {
  if (!selectedFileRef.value) {
    return
  }
  isUploadingRef.value = true
  uploadPercentRef.value = 0
  abortController = new AbortController()

  try {
    const sessionId = sessionIdRef.value ?? (sessionIdRef.value = await syncSessionStore.createSessionAsync())

    await AsyncUtil.atLeast(500, syncSessionStore.uploadFileAsync(
      sessionId,
      selectedFileRef.value,
      (loaded, total) => {
        uploadPercentRef.value = total > 0 ? Math.round((loaded / total) * 100) : 0
      },
      abortController.signal,
    ))

    // The server opens the file during refresh; only a Ready source can move on. A re-upload
    // to the same session is rejected, so a bad file discards the session and starts over.
    await syncSessionStore.refreshSessionAsync(sessionId)
    isUploadingRef.value = false
    if (syncSessionStore.getSession(sessionId)?.sourceState !== SourceState.Ready) {
      sourceErrorRef.value = sourceErrorMessage()
      await syncSessionStore.abandonSessionAsync(sessionId)
      sessionIdRef.value = undefined
      return
    }
    emit('uploaded', sessionId)
  }
  catch (error) {
    if ((error as Error).name === 'AbortError') {
      return
    }
    isUploadingRef.value = false
    unexpectedErrorEvent.trigger({ error })
  }
  finally {
    abortController = undefined
    taskPageRef.value?.setBusy(false)
  }
}

function onUpdateExisting(workspaceId: string) {
  // Re-sync happens on the tree's manage page, not here -- hand off there without uploading.
  const dataGroupId = matchesRef.value.find(m => m.workspaceId === workspaceId)?.dataGroupId
  if (dataGroupId) {
    router.push({ name: 'researchGroup.manageTab', params: { dataGroupId } })
  }
}

function onCreateNew() {
  // Commit the file as a new tree -- this starts the upload, same as a first-time selection.
  if (pendingFileRef.value) {
    const file = pendingFileRef.value
    pendingFileRef.value = undefined
    commitFile(file)
  }
}

function onCancelMatch() {
  // Didn't choose -- discard the pending file so they can pick a different one.
  pendingFileRef.value = undefined
}

function sourceErrorMessage() {
  const session = syncSessionStore.getSession(sessionIdRef.value)
  if (session?.sourceState === SourceState.Unrecognized) {
    return "We don't recognize this type of family tree file."
  }
  if (session?.isSourceOutdated) {
    const product = selectedTypeInfoRef.value?.product ?? 'family tree'
    return `This looks like an older ${product} file we can't read yet. Save it again in a newer version, then upload that.`
  }
  return "We couldn't read this file. It may be damaged or an unsupported version."
}

async function onCancelUpload() {
  const confirmed = await abandonConfirmation.ask()
  if (confirmed) {
    await abandonUploadAsync()
  }
}

async function abandonUploadAsync() {
  abortController?.abort()
  isUploadingRef.value = false
  await syncSessionStore.abandonSessionAsync(sessionIdRef.value)
  sessionIdRef.value = undefined
}

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

function handleBeforeUnload(e: BeforeUnloadEvent) {
  if (isUploadingRef.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

onBeforeRouteLeave(async () => {
  if (!isUploadingRef.value) {
    return true
  }
  const confirmed = await abandonConfirmation.ask()
  if (confirmed) {
    await abandonUploadAsync()
  }
  return confirmed
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(TaskPage, {
    ref_key: "taskPageRef",
    ref: taskPageRef,
    "no-brand": "",
    "no-auto-back": "",
    "no-back": isUploadingRef.value,
    "can-continue": !isUploadingRef.value && !!selectedFileRef.value && isFileSupportedRef.value,
    onAction: onUpload,
    onBack: _cache[3] || (_cache[3] = ($event: any) => (emit('back')))
  }, _createSlots({
    title: _withCtx(() => [
      _createTextVNode("Upload my tree")
    ]),
    actionText: _withCtx(() => [
      _createTextVNode("Upload")
    ]),
    default: _withCtx(() => [
      (sourceErrorRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(sourceErrorRef.value), 1))
        : _createCommentVNode("", true),
      (selectedFileRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (sourceRef.value)
              ? (_openBlock(), _createBlock(SyncSourceHeader, {
                  key: 0,
                  source: sourceRef.value
                }, null, 8, ["source"]))
              : _createCommentVNode("", true),
            (!isUploadingRef.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "btn btn-inline btn-link change-btn",
                  onClick: onChangeFile
                }, " Change "))
              : _createCommentVNode("", true),
            (!isFileSupportedRef.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  (selectedTypeInfoRef.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(" We're working on support for " + _toDisplayString(selectedTypeInfoRef.value.product) + ", but it's not ready yet. ", 1)
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(" We don't recognize this type of family tree file. ")
                      ], 64))
                ]))
              : _createCommentVNode("", true)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("label", {
              class: _normalizeClass(["drop-zone", { 'drag-over': isDragOverRef.value }]),
              onDragenter: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (isDragOverRef.value = true), ["prevent"])),
              onDragover: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (isDragOverRef.value = true), ["prevent"])),
              onDragleave: _cache[2] || (_cache[2] = ($event: any) => (isDragOverRef.value = false)),
              onDrop: _withModifiers(onFileDropped, ["prevent"])
            }, [
              _createElementVNode("input", {
                type: "file",
                accept: acceptAttrRef.value,
                class: "visually-hidden",
                onChange: onFileSelected
              }, null, 40, _hoisted_6),
              _hoisted_7,
              _hoisted_8
            ], 42, _hoisted_5),
            _createElementVNode("div", _hoisted_9, [
              _hoisted_10,
              _createElementVNode("div", _hoisted_11, [
                _createVNode(AppSupportGrid, { compact: "" })
              ])
            ])
          ])),
      (isUploadingRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createVNode(ProgressBar, {
              value: uploadPercentRef.value,
              max: 100,
              smooth: ""
            }, null, 8, ["value"]),
            _hoisted_13
          ]))
        : _createCommentVNode("", true),
      _createVNode(Modal, {
        ref_key: "abandonModalRef",
        ref: abandonModalRef,
        "ok-style": "danger",
        onOk: _unref(abandonConfirmation).confirm,
        onHidden: _unref(abandonConfirmation).dismiss
      }, {
        title: _withCtx(() => [
          _createTextVNode("Cancel upload?")
        ]),
        okText: _withCtx(() => [
          _createTextVNode("Cancel upload")
        ]),
        cancelText: _withCtx(() => [
          _createTextVNode("Continue uploading")
        ]),
        default: _withCtx(() => [
          _createTextVNode(" Your file is still uploading. If you cancel now, the upload will be discarded. ")
        ]),
        _: 1
      }, 8, ["onOk", "onHidden"]),
      _createVNode(WorkspaceMatchModal, {
        ref_key: "matchModalRef",
        ref: matchModalRef,
        matches: matchesRef.value,
        onUpdate: onUpdateExisting,
        onCreateNew: onCreateNew,
        onCancel: onCancelMatch
      }, null, 8, ["matches"])
    ]),
    _: 2
  }, [
    (isUploadingRef.value)
      ? {
          name: "secondaryAction",
          fn: _withCtx(() => [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-outline-primary",
              onClick: onCancelUpload
            }, " Cancel ")
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["no-back", "can-continue"]))
}
}

})